import React, { useState, useEffect } from 'react';
import { Modal, Upload, Avatar, Button, Slider } from 'antd';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { RcFile } from 'antd/es/upload';
import { useNotification } from '../contexts';
import { useUploadProfilePictureMutation } from '../store';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import Cropper from 'react-easy-crop';
import '../assets/styles/upload.css';
import GlobalLoader from './GlobalLoader';
import {
  ALLOWED_PROFILE_EXTENSIONS,
  ALLOWED_PROFILE_MAX_SIZE,
} from '../pages/TravelRequest/constants/travelreq.constants';
import { IAuthenticatedUser } from '../pages/Requisitions/constants/requisition.interface';

interface IChangeProfilePictureModalProps {
  open: boolean;
  onClose: () => void;
  initialProfilePicUrl?: string;
  refetchProfilePic?: () => void;
  user: IAuthenticatedUser;
  onProfilePictureUpdate?: (newProfilePicUrl: string | null) => void;
}

const ChangeProfilePictureModal: React.FC<IChangeProfilePictureModalProps> = ({
  open,
  onClose,
  initialProfilePicUrl,
  refetchProfilePic,
  user,
  onProfilePictureUpdate,
}) => {
  const { showNotification } = useNotification();
  const [_, setFile] = useState<RcFile | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [userProfileLoading, setUserProfileLoading] = useState(false);

  const [uploadProfilePicture, { isLoading }] =
    useUploadProfilePictureMutation();

  useEffect(() => {
    if (open) {
      setPreview(initialProfilePicUrl || null);
      if (!initialProfilePicUrl) {
        setPreview(null);
        setCropImage(null);
      }
    } else {
      setPreview(null);
      setCropImage(null);
      setShowCropper(false);
    }
  }, [open, initialProfilePicUrl]);
  const handleFileChange = async (newFile: RcFile) => {
    // File type validation
    const fileName = newFile.name.toLowerCase();
    const isAllowedType = ALLOWED_PROFILE_EXTENSIONS.some(ext =>
      fileName.endsWith(ext),
    );
    if (!isAllowedType) {
      showNotification(
        'error',
        'File Error',
        'Excel or PDF files cannot be uploaded.',
      );
    } else if (newFile.size > ALLOWED_PROFILE_MAX_SIZE) {
      // File size validation (10 MB)
      showNotification(
        'error',
        'File Error',
        `Image size must not exceed ${ALLOWED_PROFILE_MAX_SIZE / (1024 * 1024)} MB.`,
      );
    } else {
      const reader = new FileReader();
      reader.onloadstart = () => setUserProfileLoading(true);
      reader.onloadend = () => setUserProfileLoading(false);
      setFile(newFile);
      reader.readAsDataURL(newFile);
      reader.onload = async () => {
        setCropImage(reader.result as string);
        setShowCropper(true);
      };
      reader.onerror = () => {
        showNotification('error', 'File Error', 'Unable to read image file.');
      };
    }

    return false; // Prevent auto-upload
  };

  const handleRemovePicture = async () => {
    setFile(null);
    setPreview(null);
    setCropImage(null);
    try {
      const result = await uploadProfilePicture({
        id: user?.ID,
        formData: new FormData(),
      }).unwrap();
      showNotification(
        'success',
        'Profile Picture Removed',
        result.message || 'Profile photo removed successfully.',
      );
      if (refetchProfilePic) refetchProfilePic();
      if (onProfilePictureUpdate) onProfilePictureUpdate(null);
      onClose();
    } catch (err) {}
  };

  const getCroppedImg = async (
    imageSrc: string,
    cropPixels: any,
  ): Promise<Blob | null> => {
    const image = new window.Image();
    image.src = imageSrc;
    await new Promise(resolve => {
      image.onload = resolve;
    });
    const canvas = document.createElement('canvas');
    canvas.width = cropPixels.width;
    canvas.height = cropPixels.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(
      image,
      cropPixels.x,
      cropPixels.y,
      cropPixels.width,
      cropPixels.height,
      0,
      0,
      cropPixels.width,
      cropPixels.height,
    );
    return new Promise(resolve => {
      canvas.toBlob(blob => {
        resolve(blob || null);
      }, 'image/jpeg');
    });
  };

  const onCropComplete = (_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropAndSave = async () => {
    if (!cropImage || !croppedAreaPixels) return;
    setUserProfileLoading(true);
    const croppedBlob = await getCroppedImg(cropImage, croppedAreaPixels);
    if (!croppedBlob) {
      showNotification('error', 'Crop Error', 'Unable to crop image.');
      setUserProfileLoading(false);
      return;
    }
    const formData = new FormData();
    formData.append('profilePicture', croppedBlob, 'cropped.jpg');
    try {
      const result = await uploadProfilePicture({
        id: user?.ID,
        formData,
      }).unwrap();
      showNotification(
        'success',
        'Profile Picture Uploaded',
        result.message || 'Profile photo uploaded successfully',
      );
      if (refetchProfilePic) refetchProfilePic();
      if (onProfilePictureUpdate) {
        const newUrl = URL.createObjectURL(croppedBlob);
        onProfilePictureUpdate(newUrl);
      }
      setShowCropper(false);
      setCropImage(null);
      setPreview(null);
      onClose();
    } catch (err) {
      showNotification(
        'error',
        'Upload Error',
        'Failed to upload cropped image.',
      );
    }
    setUserProfileLoading(false);
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        title="Change Profile Picture"
        width={300}
        centered
        closable={true}
      >
        <div className="flex flex-col items-center justify-center p-2 gap-y-2">
          <Avatar
            size={128}
            src={preview || undefined}
            icon={
              !preview && (
                <UserCircleIcon className="size-14 cursor-pointer text-white-600" />
              )
            }
            className="mb-2 border-2 border-slate-200"
          />

          <div
            className={`flex flex-row gap-2 w-full  ${!preview ? 'justify-center' : ''}`}
          >
            {preview ? (
              <>
                <Upload
                  beforeUpload={file => handleFileChange(file)}
                  showUploadList={false}
                  accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.tiff"
                  className="flex-1"
                >
                  <Button
                    icon={<UploadOutlined />}
                    className="w-full !text-xs !py-2"
                    size="middle"
                    loading={isLoading}
                  >
                    Change Picture
                  </Button>
                </Upload>

                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleRemovePicture}
                  className="flex-1 !text-xs !py-2"
                  size="middle"
                  disabled={isLoading}
                  loading={isLoading}
                >
                  Remove
                </Button>
              </>
            ) : (
              <Upload
                className="w-full"
                beforeUpload={file => handleFileChange(file)}
                showUploadList={false}
                accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.tiff"
              >
                <Button
                  icon={<UploadOutlined />}
                  className="!text-xs !py-2 w-full"
                  size="middle"
                  loading={isLoading}
                >
                  Upload Picture
                </Button>
              </Upload>
            )}
          </div>
        </div>
      </Modal>

      {/* Cropper Modal */}
      <Modal
        open={showCropper}
        onCancel={() => {
          setShowCropper(false);
          setCropImage(null);
          setUserProfileLoading(false);
        }}
        footer={null}
        title="Crop Image"
        width={400}
        centered
        closable={true}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: 300,
            background: '#222',
          }}
        >
          {cropImage && (
            <Cropper
              image={cropImage}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>
        <div className="mt-4">
          <span>Zoom: </span>
          <Slider min={1} max={3} step={0.1} value={zoom} onChange={setZoom} />
        </div>
        <div className="flex justify-end mt-4 gap-2">
          <Button
            onClick={() => {
              setShowCropper(false);
              setCropImage(null);
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleCropAndSave}
            loading={isLoading}
          >
            Crop & Save
          </Button>
        </div>
      </Modal>
      <GlobalLoader loading={userProfileLoading} />
    </>
  );
};

export default ChangeProfilePictureModal;
